import usePrefix from '@/hooks/usePrefix';
import { Button, Picker } from '@/components/Bicester';
import React from 'react';
import { ControlLabel, FormGroup } from 'rsuite';
import { ItemDataType } from 'rsuite5/esm/@types/common';
import options from '@/utils/options';
import { FormControl } from '@/components/formik-rsuite';
import { SelectPickerProps } from 'rsuite5';
import _ from 'lodash';
import { FORM_DEFAULT_DISPLAY } from '@/constants/config';

type PickerType = 'TorF' | 'TorFWithStr' | 'sex' | 'level' | 'levelNotYishang' | 'village';

const getPickerData = (pickerType: PickerType) =>
  options<{ label: string; value: string | number }[], string>(
    {
      TorF: () => [
        { label: '是', value: 1 },
        { label: '否', value: 0 },
      ],
      TorFWithStr: () => [
        { label: '是', value: '是' },
        { label: '否', value: '否' },
      ],
      sex: () => [
        { label: '男', value: '男' },
        { label: '女', value: '女' },
      ],
      level: () => [
        { label: '奕友', value: 1 },
        { label: '奕享', value: 2 },
        { label: '奕尊', value: 3 },
        { label: '奕尚', value: 4 },
      ],
      levelNotYishang: () => [
        { label: '待激活', value: 0 },
        { label: '奕友', value: 1 },
        { label: '奕享', value: 2 },
        { label: '奕尊', value: 3 },
      ],
      village: () => [
        { label: '比斯特苏州购物村', value: 1 },
        { label: '比斯特上海购物村', value: 2 },
      ],
    },
    pickerType
  );

export const getLabel = (value: any, pickerData: ItemDataType[]) => {
  return pickerData.find((item) => item.value === value)?.label ?? '';
};

const BicesterCardPicker = ({
  className,
  isEdit = true,
  notEditable = false,
  required = false,
  cleanable = true,
  placeholder,
  label,
  value,
  formKey,
  pickerType,
  disabledItemValues,
  pickerData = [],
  hasBtn = false,
  showBtn = true,
  btnDisabled,
  btnLoading,
  btnLabel,
  customBtn,
  onClick,
  menuMaxHeight = 250,
  labelWeight,
  contentWeight,
  ...props
}: {
  className?: string;
  isEdit?: boolean;
  notEditable?: boolean; // true为不可编辑
  required?: boolean; // 是否必填
  cleanable?: boolean;
  placeholder?: React.ReactNode;
  label?: string;
  formKey: string; // 与field.name一致，只读数据中也要传个key，否则accepter报错
  value?: string | number; // field.value为空或数据不在下拉框中时显示的value，一般用于只读状态显示
  pickerType?: PickerType;
  disabledItemValues?: any[];
  pickerData?: ItemDataType[];
  hasBtn?: boolean;
  showBtn?: boolean;
  btnDisabled?: boolean;
  btnLoading?: boolean;
  btnLabel?: React.ReactNode;
  customBtn?: React.ReactNode;
  onClick?: () => void;
  menuMaxHeight?: number;
  labelWeight?: number; // label的flex占比
  contentWeight?: number; // content的flex占比
} & Omit<SelectPickerProps<any>, 'data'>) => {
  const { prefix, classname } = usePrefix('bicester-card-item');

  return (
    <div className={`bicester-card-item-wrap ${className ?? ''}`}>
      <FormGroup className={`${classname} bicester-card-picker`}>
        {label && (
          <ControlLabel
            className={prefix('label')}
            style={{ ...(labelWeight && { flex: labelWeight }) }}
          >
            <div className={prefix('label-text')}>{label}</div>
            <div
              className={`${prefix('label-divider')} ${isEdit && required ? 'required' : ''}`}
            ></div>
          </ControlLabel>
        )}
        <div
          className={`${prefix('content-wrap')} ${isEdit ? prefix('content-wrap-isedit') : ''}`}
          style={{ ...(contentWeight && { flex: contentWeight }) }}
        >
          {isEdit && !notEditable ? (
            <FormControl
              className={prefix('content')}
              name={formKey}
              accepter={Picker}
              placeholder={placeholder}
              data={pickerType ? getPickerData(pickerType) : pickerData || []}
              cleanable={cleanable}
              disabledItemValues={disabledItemValues}
              menuMaxHeight={menuMaxHeight}
              {...props}
            />
          ) : (
            <FormControl
              name={formKey}
              accepter={({ field }) => (
                <div className={prefix('content-wrap-text')}>
                  {getLabel(
                    _.isNil(field.value) ? value : field.value,
                    pickerType ? getPickerData(pickerType) : pickerData
                  ) ||
                    value ||
                    FORM_DEFAULT_DISPLAY}
                </div>
              )}
            />
          )}
          {hasBtn && showBtn && (
            <div className="flex items-center">
              {customBtn || (
                <Button
                  type="submit"
                  className={prefix('content-btn')}
                  disabled={btnDisabled}
                  loading={btnLoading}
                  onClick={onClick}
                >
                  {btnLabel}
                </Button>
              )}
            </div>
          )}
        </div>
      </FormGroup>
    </div>
  );
};

export default BicesterCardPicker;
